import React from 'react'
import {
  useRef,
  useMemo,
  useEffect,
  useState,
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  DragEvent as ReactDragEvent,
} from 'react'
import classNames from 'classnames'

import styles from './style.module.scss'
import privateIcon from 'assets/images/calendar/private-16.svg'
import recurringIcon from 'assets/images/calendar/recurrence-16.svg'
import exceptionIcon from 'assets/images/calendar/exception-16.svg'
import attachmentIcon from 'assets/images/calendar/attachment-16.svg'

import settings from 'popups/Settings'

interface EventStrechable {
  top?: boolean
  bottom?: boolean
  left?: boolean
  right?: boolean
}

interface CalendarEvent {
  instanceId?: string
  title: string
  subject?: string
  location?: string
  description?: string
  startDate: Date
  endDate?: Date
  duration?: number
  allday?: boolean
  isOverCount?: boolean
  isPrivate?: boolean
  isRecurring?: boolean
  hasException?: boolean
  hasAttachment?: boolean
  isDeclined?: boolean
  isCanceled?: boolean
  isNotResponse?: boolean
  isTodo?: boolean
  isMyEvent?: boolean
  isFree?: boolean
  folderColor: string
  color: string
  style: CSSProperties
  strechable?: EventStrechable
  useDragImage?: boolean
  event: {
    start: Date
    rrule?: any
  }
  exception?: {
    recurrenceid?: Date
  }
}

interface StrechStartState {
  started: boolean
  direction: 'top' | 'bottom' | 'left' | 'right' | null
}

interface EventCellProps {
  overCountId?: string
  event: CalendarEvent
  onCellClick?: ((event: CalendarEvent, element: HTMLDivElement | null) => void) | null
  onCellDoubleClick?: ((event: CalendarEvent, element: HTMLDivElement | null) => void) | null
  forDay?: boolean
  draggable?: boolean
  onStrechStart?:
    | ((event: CalendarEvent, isTop: boolean, isLeft: boolean, e: ReactMouseEvent) => void)
    | null
  onStrechMove?:
    | ((event: CalendarEvent, isTop: boolean, isLeft: boolean, e: MouseEvent) => void)
    | null
  onStrechEnd?:
    | ((
        event: CalendarEvent,
        isTop: boolean,
        isLeft: boolean,
        e: MouseEvent | KeyboardEvent,
        cancelled: boolean,
      ) => void)
    | null
  readOnly?: boolean
}

const gIsStreching = {
  streching: false,
}

const EventCell = ({
  overCountId,
  event,
  onCellClick = null,
  onCellDoubleClick = null,
  forDay = false,
  draggable = true,
  onStrechStart = null,
  onStrechMove = null,
  onStrechEnd = null,
  readOnly = false,
}: EventCellProps) => {
  const me = useRef<HTMLDivElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const locationRef = useRef<HTMLDivElement>(null)
  const iconRef = useRef<HTMLDivElement>(null)
  const [strechStart, setStrechStart] = useState<StrechStartState>({
    started: false,
    direction: null,
  })

  const [canDrag, setCanDrag] = useState(!readOnly)
  const isMobileView = settings.getStore().getState().appsettings.isMobileView
  useEffect(() => {
    if (isMobileView) {
      setCanDrag(false)
    } else {
      setCanDrag(!readOnly && !event.isTodo && event.isMyEvent)
    }
  }, [isMobileView, event.isTodo, event.isMyEvent, readOnly])

  const isTimeOver = useMemo(() => {
    return !event.isOverCount && event.endDate?.valueOf() < new Date().valueOf()
  }, [event])

  const hasIcon = useMemo(() => {
    return forDay && (event.isPrivate || event.isRecurring || event.hasAttachment)
  }, [forDay, event.isPrivate, event.isRecurring, event.hasAttachment])

  const getIcons = (forMultiLine = false) => {
    let iconCount = 0

    if (event.isPrivate) iconCount++
    if (event.isRecurring) iconCount++
    if (event.hasAttachment) iconCount++

    const width = iconCount * 16

    return (
      <div
        className={classNames(styles.eventCellIconBox, forMultiLine ? styles.multiLine : '')}
        style={{ width: `${width}px` }}
        ref={forMultiLine ? iconRef : null}
      >
        {event.isPrivate && <img src={privateIcon} alt="private" />}
        {event.isRecurring && !event.hasException && <img src={recurringIcon} alt="recurring" />}
        {event.hasException && <img src={exceptionIcon} alt="exception" />}
        {event.hasAttachment && <img src={attachmentIcon} alt="attachment" />}
      </div>
    )
  }

  const getContents = () => {
    if (event.allday || event.isOverCount) {
      return (
        <div
          className={classNames(
            styles.eventCellTitle,
            event.isDeclined || event.isCanceled ? styles.canceled : '',
            event.isOverCount ? styles.overCount : styles.allday,
            event.isNotResponse && event.allday ? styles.invited : '',
          )}
        >
          {event.title}
        </div>
      )
    }

    if (!forDay) {
      // 1 Line event - month view. day events list.
      return (
        <div
          className={classNames(
            styles.eventCellTitle,
            event.isDeclined || event.isCanceled ? styles.canceled : '',
            styles.oneline,
          )}
        >
          {event.title}
        </div>
      )
    }

    // 1 Line event - 30분 이하 location 없음.
    if (event.duration && event.duration <= 30) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <div
            className={classNames(
              styles.eventCellTitle,
              event.isDeclined || event.isCanceled ? styles.canceled : '',
              styles.oneline,
            )}
          >
            {event.title}
          </div>
          {hasIcon && getIcons()}
        </div>
      )
    }

    return (
      <div className={styles.eventCellContentsContainer}>
        <div ref={contentsRef} style={{ width: 'calc(100% - 4px)', height: 'calc(100% - 8px)' }}>
          <div className={styles.eventCellTitle} ref={titleRef}>
            {event.title}
          </div>
          {event.location && (
            <div className={styles.eventCellLocation} ref={locationRef}>
              {event.location}
            </div>
          )}
          {hasIcon && getIcons(true)}
        </div>
      </div>
    )
  }

  useEffect(() => {
    const cRef = contentsRef.current
    if (!cRef) return

    const { clientWidth, clientHeight } = cRef
    let height = clientHeight

    if (hasIcon) {
      height -= 16 + 2 // 2 bottom
    }

    let remainHeight = -1
    if (titleRef.current) {
      //Line Height 14.3
      const titleHeight = titleRef.current.clientHeight
      remainHeight = height - titleHeight
      if (remainHeight < 17.2 /* ocation height (13.2 + top margin 4) */) {
        const titleLineCount = Math.max(1, Math.floor(height / 14.3))
        let calcTitleHeight = titleLineCount * 14.3
        if (calcTitleHeight > titleHeight) {
          calcTitleHeight = titleHeight
        }
        titleRef.current.style.height = `${calcTitleHeight}px`
      }
    }

    if (locationRef.current) {
      const iRef = iconRef.current
      // LineHeight 13.2 + top margin 4
      if (remainHeight < 17.2) {
        const locationWidth = clientWidth - (iRef ? iRef.clientWidth - 2 : 0)
        locationRef.current.style.width = `${locationWidth}px`
        locationRef.current.style.height = `${13.2}px`
      } else {
        const locationLineCount = Math.floor((remainHeight - 4) / 13.2)
        locationRef.current.style.height = `${locationLineCount * 13.2}px`
      }
    }
  }, [event, hasIcon])

  useEffect(() => {
    const onMouseUp = (e: MouseEvent) => {
      e.preventDefault()
      onStrechEnd?.(
        event,
        strechStart.direction === 'top',
        strechStart.direction === 'left',
        e,
        false,
      )
      setStrechStart({ started: false, direction: null })
      gIsStreching.streching = false
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onStrechEnd?.(
          event,
          strechStart.direction === 'top',
          strechStart.direction === 'left',
          e,
          true,
        )
        setStrechStart({ started: false, direction: null })
        gIsStreching.streching = false
      }
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!strechStart.started) {
        return
      }
      onStrechMove?.(event, strechStart.direction === 'top', strechStart.direction === 'left', e)
    }

    if (strechStart.started && (onStrechStart || onStrechMove || onStrechEnd) && !readOnly) {
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('keydown', onKeyDown)
      return () => {
        document.removeEventListener('mouseup', onMouseUp)
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('keydown', onKeyDown)
      }
    }
  }, [strechStart, readOnly, event, onStrechStart, onStrechMove, onStrechEnd])

  const doubleClicked = useRef(false)
  const clickTimer = useRef<NodeJS.Timeout | null>(null)
  const handleClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (clickTimer.current !== null) {
      return
    }
    clickTimer.current = setTimeout(() => {
      if (!doubleClicked.current) {
        console.log('event cell click timeout')
        if (onCellClick) onCellClick(event, me.current)
      }
      doubleClicked.current = false
      clickTimer.current = null
    }, 300)
  }

  const handleDoubleClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    console.log('event cell double click')
    doubleClicked.current = true

    onCellDoubleClick?.(event, me.current)
  }

  return (
    <div
      ref={me}
      className={classNames(
        styles.eventCell,
        (event.strechable?.top ||
          event.strechable?.bottom ||
          event.strechable?.left ||
          event.strechable?.right) &&
          canDrag
          ? styles.strechable
          : '',
        strechStart.started
          ? strechStart.direction === 'top'
            ? styles.strechingTop
            : strechStart.direction === 'left'
              ? styles.strechingLeft
              : strechStart.direction === 'bottom'
                ? styles.strechingBottom
                : strechStart.direction === 'right'
                  ? styles.strechingRight
                  : ''
          : '',
        readOnly ? styles.readOnly : '',
      )}
      style={{ ...event.style }}
      onMouseDown={
        !readOnly
          ? (e: ReactMouseEvent) => {
              e.stopPropagation()
            }
          : undefined
      }
      onMouseOver={
        !readOnly
          ? () => {
              if (
                draggable &&
                canDrag &&
                me.current &&
                me.current.parentNode &&
                !gIsStreching.streching
              ) {
                const c = me.current.parentNode.children
                if (c[c.length - 1] !== me.current) {
                  me.current.parentNode.appendChild(me.current)
                }
              }
            }
          : undefined
      }
      onClick={!readOnly && onCellClick ? handleClick : undefined}
      onDoubleClick={!readOnly && onCellDoubleClick ? handleDoubleClick : undefined}
      id={event.instanceId || overCountId}
      draggable={!readOnly && draggable && canDrag && !event.isOverCount}
      onDragStart={(e: ReactDragEvent) => {
        if (!event.useDragImage) {
          const img = document.getElementById('dragImage')
          if (img) {
            e.dataTransfer.setDragImage(img, 0, 0)
          }
        }
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.clearData()
        e.dataTransfer.setData('data', event.instanceId || '')
      }}
    >
      <div
        className={`${styles.container} ${event.isOverCount || event.allday ? '' : styles.normal} ${event.isNotResponse ? styles.notResponse : ''}`}
        style={
          {
            '--bg-color': event.color + (event.isOverCount ? '' : event.allday ? 'ff' : '56'),
            '--opacity': isTimeOver ? 0.4 : 1,
            '--color': event.color,
          } as CSSProperties
        }
      >
        {!event.isOverCount && (
          <div
            className={classNames(
              styles.eventCellBelt,
              event.isFree && !event.allday ? styles.free : '',
            )}
            style={{ '--color': event.folderColor } as CSSProperties}
          >
            &nbsp;
          </div>
        )}
        {getContents()}
        {!readOnly &&
          ((event.strechable?.top && canDrag) ||
            (strechStart.started && strechStart.direction === 'top')) &&
          !gIsStreching.streching && (
            <div
              className={`${styles.strechHandle} ${styles.strechHandleTop}`}
              onMouseDown={(e: ReactMouseEvent) => {
                e.preventDefault()
                setStrechStart({ started: true, direction: 'top' })
                onStrechStart?.(event, true, false, e)
                gIsStreching.streching = true
              }}
            ></div>
          )}
        {!readOnly &&
          ((event.strechable?.bottom && canDrag) ||
            (strechStart.started && strechStart.direction === 'bottom')) &&
          !gIsStreching.streching && (
            <div
              className={`${styles.strechHandle} ${styles.strechHandleBottom}`}
              onMouseDown={(e: ReactMouseEvent) => {
                e.preventDefault()
                setStrechStart({ started: true, direction: 'bottom' })
                onStrechStart?.(event, false, false, e)
                gIsStreching.streching = true
              }}
            ></div>
          )}
        {!readOnly &&
          ((event.strechable?.left && canDrag) ||
            (strechStart.started && strechStart.direction === 'left')) &&
          !gIsStreching.streching && (
            <div
              className={`${styles.strechHandle} ${styles.strechHandleLeft}`}
              onMouseDown={(e: ReactMouseEvent) => {
                e.preventDefault()
                setStrechStart({ started: true, direction: 'left' })
                onStrechStart?.(event, false, true, e)
                gIsStreching.streching = true
              }}
            ></div>
          )}
        {!readOnly &&
          ((event.strechable?.right && canDrag) ||
            (strechStart.started && strechStart.direction === 'right')) &&
          !gIsStreching.streching && (
            <div
              className={`${styles.strechHandle} ${styles.strechHandleRight}`}
              onMouseDown={(e: ReactMouseEvent) => {
                e.preventDefault()
                setStrechStart({ started: true, direction: 'right' })
                onStrechStart?.(event, false, false, e)
                gIsStreching.streching = true
              }}
            ></div>
          )}
      </div>
    </div>
  )
}

export default EventCell
